import { DomainError, TransportError } from "@econ-v1/domain";
import { MAX_CLOSE_REASON_BYTES, MAX_DTLS_FINGERPRINT_BYTES, MAX_ICE_CANDIDATE_BYTES, MAX_OFFER_NONCE_BYTES, MAX_SDP_BYTES, MAX_SDP_MID_BYTES, MIN_OFFER_NONCE_BYTES, WEBRTC_SIGNAL_VERSION, } from "@econ-v1/ports";
export class WebRtcSignalingWsAdapter {
    #baseUrl;
    #websocketFactory;
    #pendingAnswers = new Map();
    #socket;
    #closed = false;
    constructor(options) {
        this.#baseUrl = new URL(options.baseUrl);
        this.#websocketFactory = options.websocketFactory ?? defaultWebSocketFactory;
    }
    async connect(setupHeaders, signal) {
        if (this.#closed)
            throw new TransportError({ code: "offline", message: "webrtc signaling adapter is closed" });
        const socket = this.#websocketFactory(this.#signalingUrl());
        this.#socket = socket;
        socket.addEventListener("message", (event) => this.#onMessage(event));
        socket.addEventListener("close", () => this.#onClose());
        let settled = false;
        await new Promise((resolve, reject) => {
            const onOpen = () => {
                if (settled)
                    return;
                settled = true;
                socket.send(JSON.stringify({
                    headers: setupHeaders,
                    type: "setup",
                    version: WEBRTC_SIGNAL_VERSION,
                }));
                resolve();
            };
            const onFailure = () => {
                if (settled)
                    return;
                settled = true;
                reject(new TransportError({ code: "offline", message: "webrtc signaling connection failed" }));
            };
            socket.addEventListener("open", onOpen);
            socket.addEventListener("error", onFailure);
            socket.addEventListener("close", onFailure);
            if (signal?.aborted === true)
                onFailure();
        });
    }
    async sendOffer(offer) {
        const message = { ...offer, type: "offer", version: WEBRTC_SIGNAL_VERSION };
        validateOfferBounds(message);
        return await this.#sendAndAwaitAnswer(message.offer_nonce, message);
    }
    async sendCandidate(candidate) {
        const message = { ...candidate, type: "candidate" };
        validateCandidateBounds(message);
        this.#send(message);
    }
    async sendRestart(restart) {
        const message = { ...restart, type: "restart", version: WEBRTC_SIGNAL_VERSION };
        validateRestartBounds(message);
        return await this.#sendAndAwaitAnswer(message.offer_nonce, message);
    }
    async close(close) {
        if (this.#closed)
            return;
        const message = { ...close, type: "close" };
        validateCloseBounds(message);
        this.#send(message);
        this.#onClose();
    }
    #send(message) {
        if (this.#closed || this.#socket === undefined) {
            throw new TransportError({ code: "offline", message: "webrtc signaling connection is not open" });
        }
        this.#socket.send(JSON.stringify(message));
    }
    async #sendAndAwaitAnswer(offerNonce, message) {
        const answer = new Promise((resolve, reject) => {
            this.#pendingAnswers.set(offerNonce, { reject, resolve });
        });
        this.#send(message);
        return await answer;
    }
    #onMessage(event) {
        const payload = messageData(event);
        if (payload === undefined)
            return;
        let parsed;
        try {
            parsed = JSON.parse(payload);
        }
        catch {
            return;
        }
        if (!isAnswer(parsed))
            return;
        const pending = this.#pendingAnswers.get(parsed.offer_nonce);
        if (pending === undefined)
            return;
        this.#pendingAnswers.delete(parsed.offer_nonce);
        pending.resolve(parsed);
    }
    #onClose() {
        if (this.#closed)
            return;
        this.#closed = true;
        const error = new TransportError({ code: "offline", message: "webrtc signaling connection closed" });
        for (const [nonce, pending] of this.#pendingAnswers) {
            this.#pendingAnswers.delete(nonce);
            pending.reject(error);
        }
        this.#socket?.close();
        this.#socket = undefined;
    }
    #signalingUrl() {
        const protocol = this.#baseUrl.protocol === "https:" ? "wss:" : "ws:";
        const url = new URL("/webrtc-signal", this.#baseUrl);
        url.protocol = protocol;
        return url.toString();
    }
}
function defaultWebSocketFactory(url) {
    return new WebSocket(url);
}
function messageData(event) {
    if (typeof event !== "object" || event === null)
        return undefined;
    const data = event.data;
    return typeof data === "string" ? data : undefined;
}
function isAnswer(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const record = value;
    return (record.type === "answer" &&
        typeof record.offer_nonce === "string" &&
        typeof record.sdp === "string" &&
        typeof record.dtls_fingerprint === "string");
}
function validateOfferNonce(nonce) {
    if (nonce.length < MIN_OFFER_NONCE_BYTES || nonce.length > MAX_OFFER_NONCE_BYTES) {
        throw new DomainError({ code: "invalid_request", message: "webrtc offer nonce length is out of bounds" });
    }
    if (!/^[A-Za-z0-9_-]+$/.test(nonce)) {
        throw new DomainError({ code: "invalid_request", message: "webrtc offer nonce has an invalid charset" });
    }
}
function validateSdp(sdp) {
    if (sdp.length === 0)
        throw new DomainError({ code: "invalid_request", message: "webrtc sdp must not be empty" });
    if (sdp.length > MAX_SDP_BYTES) {
        throw new DomainError({ code: "invalid_request", message: "webrtc sdp exceeds the maximum size" });
    }
}
const DTLS_FINGERPRINT_PATTERN = /^(sha-256|sha-384|sha-512) ([0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2})*)$/;
const DTLS_FINGERPRINT_PAIR_COUNTS = { "sha-256": 32, "sha-384": 48, "sha-512": 64 };
function validateDtlsFingerprint(fingerprint) {
    if (fingerprint.length === 0 || fingerprint.length > MAX_DTLS_FINGERPRINT_BYTES) {
        throw new DomainError({ code: "invalid_request", message: "webrtc dtls fingerprint is malformed" });
    }
    const match = DTLS_FINGERPRINT_PATTERN.exec(fingerprint);
    const algorithm = match?.[1];
    const hexPairs = match?.[2];
    if (algorithm === undefined || hexPairs === undefined) {
        throw new DomainError({ code: "invalid_request", message: "webrtc dtls fingerprint is malformed" });
    }
    const expectedPairs = DTLS_FINGERPRINT_PAIR_COUNTS[algorithm];
    if (hexPairs.split(":").length !== expectedPairs) {
        throw new DomainError({ code: "invalid_request", message: "webrtc dtls fingerprint length does not match its algorithm" });
    }
}
function validateOfferBounds(offer) {
    validateOfferNonce(offer.offer_nonce);
    validateSdp(offer.sdp);
    validateDtlsFingerprint(offer.dtls_fingerprint);
}
function validateRestartBounds(restart) {
    validateOfferNonce(restart.previous_offer_nonce);
    validateOfferNonce(restart.offer_nonce);
    validateSdp(restart.sdp);
    validateDtlsFingerprint(restart.dtls_fingerprint);
}
function validateCandidateBounds(candidate) {
    validateOfferNonce(candidate.offer_nonce);
    if (candidate.candidate.length === 0) {
        throw new DomainError({ code: "invalid_request", message: "webrtc ice candidate must not be empty" });
    }
    if (candidate.candidate.length > MAX_ICE_CANDIDATE_BYTES) {
        throw new DomainError({ code: "invalid_request", message: "webrtc ice candidate exceeds the maximum size" });
    }
    if (candidate.sdp_mid !== undefined && candidate.sdp_mid.length > MAX_SDP_MID_BYTES) {
        throw new DomainError({ code: "invalid_request", message: "webrtc sdp_mid exceeds the maximum size" });
    }
}
function validateCloseBounds(close) {
    if (close.offer_nonce !== undefined)
        validateOfferNonce(close.offer_nonce);
    if (close.reason !== undefined && close.reason.length > MAX_CLOSE_REASON_BYTES) {
        throw new DomainError({ code: "invalid_request", message: "webrtc close reason exceeds the maximum size" });
    }
}
//# sourceMappingURL=webrtc-signaling-ws-adapter.js.map